import React, { useEffect, useState, useCallback } from 'react';
import { analyticsAPI } from '../services/api';

const PLATFORMS = [
  { key: 'YOUTUBE',   label: 'YouTube',    icon: '▶',  color: '#ff0000', metric: 'Subscribers' },
  { key: 'INSTAGRAM', label: 'Instagram',  icon: '📸', color: '#c13584', metric: 'Followers'   },
  { key: 'TWITTER',   label: 'Twitter/X',  icon: '𝕏',  color: '#141414', metric: 'Followers'   },
  { key: 'LINKEDIN',  label: 'LinkedIn',   icon: 'in', color: '#0077b5', metric: 'Followers'   },
  { key: 'FACEBOOK',  label: 'Facebook',   icon: '👍', color: '#1877f2', metric: 'Followers'   },
];

const MONTHS = [
  '2025-05','2025-04','2025-03','2025-02','2025-01',
  '2024-12','2024-11','2024-10','2024-09','2024-08','2024-07','2024-06',
];

const emptySnap = {
  platform: 'YOUTUBE', period: new Date().toISOString().slice(0,7),
  followers: '', newFollowers: '', totalViews: '', avgViewsPerPost: '',
  totalPosts: '', totalLikes: '', totalComments: '',
  engagementRate: '', estimatedReach: '', notes: '',
};

export default function AnalyticsPage() {
  const [summary, setSummary]     = useState(null);
  const [allData, setAllData]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(emptySnap);
  const [saving, setSaving]       = useState(false);
  const [activePlatform, setActivePlatform] = useState('YOUTUBE');
  const [view, setView]           = useState('overview'); // 'overview' | 'log'

  const load = useCallback(async () => {
    try {
      const [sumRes, allRes] = await Promise.all([
        analyticsAPI.getSummary(),
        analyticsAPI.getAll(),
      ]);
      setSummary(sumRes.data);
      setAllData(allRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const f = (updates) => setForm(p => ({ ...p, ...updates }));

  const handleSave = async () => {
    if (!form.platform) return;
    setSaving(true);
    try {
      await analyticsAPI.save(form);
      setShowForm(false);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this snapshot?')) return;
    await analyticsAPI.delete(id);
    load();
  };

  const openLog = (platform = 'YOUTUBE') => {
    setForm({ ...emptySnap, platform });
    setShowForm(true);
  };

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading…</div>;

  const cards     = summary?.platformCards || [];
  const trends    = summary?.trends        || {};
  const hasData   = cards.length > 0;

  return (
    <div style={{ padding: '2rem', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e' }}>Analytics 📊</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.875rem' }}>
            Track your growth across all platforms — log monthly stats
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {['overview', 'log'].map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: '0.5rem 1rem', border: '1.5px solid', borderRadius: 10,
              fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
              background: view === v ? '#1a7a4a' : 'white',
              color: view === v ? 'white' : '#1a7a4a',
              borderColor: '#1a7a4a',
            }}>
              {v === 'overview' ? '📊 Overview' : '📝 Log History'}
            </button>
          ))}
          <button onClick={() => openLog()} style={primaryBtn}>+ Log Stats</button>
        </div>
      </div>

      {/* Tip */}
      <div style={{ background: '#e8f5e9', border: '1px solid #c8ebd8', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.82rem', color: '#2e7d32' }}>
        💡 Log your stats at the <b>end of each month</b> for accurate growth tracking. Get data from YouTube Studio, Instagram Insights, etc.
      </div>

      {!hasData && (
        <div style={emptyState}>
          <div style={{ fontSize: '3rem' }}>📊</div>
          <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1.1rem' }}>No analytics data yet</div>
          <div style={{ color: '#888', fontSize: '0.875rem', margin: '0.4rem 0 1rem' }}>Start by logging your stats for this month</div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {PLATFORMS.map(p => (
              <button key={p.key} onClick={() => openLog(p.key)} style={{ ...outlineBtn, fontSize: '0.8rem', padding: '0.5rem 0.9rem' }}>
                {p.icon} {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {hasData && view === 'overview' && (
        <>
          {/* ── Total Reach Summary ─────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={summaryCard('#1a1a2e')}>
              <div style={{ fontSize: '0.72rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Audience</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white' }}>{formatNum(summary?.totalFollowers)}</div>
              <div style={{ fontSize: '0.75rem', color: '#888' }}>across all platforms</div>
            </div>
            <div style={summaryCard('#1a7a4a')}>
              <div style={{ fontSize: '0.72rem', color: '#a8d8b9', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Views This Month</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white' }}>{formatNum(summary?.totalViewsMonth)}</div>
              <div style={{ fontSize: '0.75rem', color: '#a8d8b9' }}>{summary?.currentPeriod}</div>
            </div>
            <div style={summaryCard('#1565c0')}>
              <div style={{ fontSize: '0.72rem', color: '#90caf9', textTransform: 'uppercase', letterSpacing: '0.05em' }}>New Followers</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white' }}>
                {(summary?.totalNewFollowers || 0) >= 0 ? '+' : ''}{formatNum(summary?.totalNewFollowers)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#90caf9' }}>this month</div>
            </div>
            {summary?.bestPlatform && (
              <div style={summaryCard('#7b1fa2')}>
                <div style={{ fontSize: '0.72rem', color: '#ce93d8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Best Platform</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'white' }}>
                  {PLATFORMS.find(p => p.key === summary.bestPlatform)?.icon} {summary.bestPlatform}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#ce93d8' }}>most followers</div>
              </div>
            )}
          </div>

          {/* ── Platform Cards ───────────────────────────────────── */}
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 800, color: '#1a1a2e' }}>Platform Breakdown</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.75rem' }}>
            {cards.map(card => {
              const meta = PLATFORMS.find(p => p.key === card.platform);
              if (!meta) return null;
              const growth = card.newFollowers || 0;
              return (
                <div key={card.platform} style={{ background: 'white', borderRadius: 16, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: `3px solid ${meta.color}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ fontWeight: 800, color: meta.color, fontSize: '0.9rem' }}>{meta.icon} {meta.label}</span>
                    <span style={{ fontSize: '0.7rem', color: '#aaa' }}>{card.period}</span>
                  </div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1a1a2e' }}>{formatNum(card.followers)}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.6rem' }}>{meta.metric}</div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.78rem' }}>
                    {growth !== 0 && (
                      <div style={{ color: growth > 0 ? '#2e7d32' : '#c62828', fontWeight: 700 }}>
                        {growth > 0 ? '▲' : '▼'} {Math.abs(growth).toLocaleString('en-IN')} this month
                      </div>
                    )}
                    {card.totalViews && <div><span style={{ color: '#888' }}>Views: </span><b>{formatNum(card.totalViews)}</b></div>}
                    {card.avgViewsPerPost && <div><span style={{ color: '#888' }}>Avg/post: </span><b>{formatNum(card.avgViewsPerPost)}</b></div>}
                    {card.engagementRate && <div><span style={{ color: '#888' }}>Engagement: </span><b>{card.engagementRate}%</b></div>}
                    {card.totalPosts && <div><span style={{ color: '#888' }}>Posts: </span><b>{card.totalPosts}</b></div>}
                  </div>

                  <button onClick={() => openLog(card.platform)}
                    style={{ marginTop: '0.85rem', width: '100%', padding: '0.4rem', background: `${meta.color}15`, border: `1px solid ${meta.color}44`, borderRadius: 8, color: meta.color, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>
                    + Update Stats
                  </button>
                </div>
              );
            })}
            {/* Add missing platforms */}
            {PLATFORMS.filter(p => !cards.find(c => c.platform === p.key)).map(p => (
              <div key={p.key} style={{ background: '#fafafa', borderRadius: 16, padding: '1.25rem', border: '2px dashed #e0e0e0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', minHeight: 160 }}>
                <span style={{ fontSize: '1.5rem', color: '#ccc' }}>{p.icon}</span>
                <span style={{ fontSize: '0.8rem', color: '#aaa', fontWeight: 700 }}>{p.label}</span>
                <button onClick={() => openLog(p.key)} style={{ padding: '0.35rem 0.75rem', background: 'white', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', color: '#666' }}>
                  + Add
                </button>
              </div>
            ))}
          </div>

          {/* ── Growth Trend Charts (CSS) ─────────────────────────── */}
          {Object.keys(trends).length > 0 && (
            <>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 800, color: '#1a1a2e' }}>Growth Trends</h3>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {PLATFORMS.filter(p => trends[p.key]).map(p => (
                  <button key={p.key} onClick={() => setActivePlatform(p.key)} style={{
                    padding: '0.4rem 0.85rem', borderRadius: 20, border: '1.5px solid',
                    fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                    background: activePlatform === p.key ? p.color : 'white',
                    color: activePlatform === p.key ? 'white' : p.color,
                    borderColor: p.color,
                  }}>{p.icon} {p.label}</button>
                ))}
              </div>

              {trends[activePlatform] && (
                <div style={{ background: 'white', borderRadius: 16, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1.5rem' }}>
                  <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
                    {PLATFORMS.find(p => p.key === activePlatform)?.label} — Follower Growth (Last 6 months)
                  </div>
                  <BarChart data={trends[activePlatform]} color={PLATFORMS.find(p => p.key === activePlatform)?.color} />

                  {/* Views trend */}
                  {trends[activePlatform].some(d => d.views) && (
                    <>
                      <div style={{ fontWeight: 700, color: '#1a1a2e', margin: '1.5rem 0 1rem', fontSize: '0.9rem' }}>
                        Monthly Views / Reach
                      </div>
                      <BarChart data={trends[activePlatform]} valueKey="views"
                        color={PLATFORMS.find(p => p.key === activePlatform)?.color} />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Log History View ─────────────────────────────────────── */}
      {hasData && view === 'log' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {allData.map(snap => {
            const meta = PLATFORMS.find(p => p.key === snap.platform);
            return (
              <div key={snap.id} style={{ background: 'white', borderRadius: 12, padding: '0.9rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: meta?.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: '#1a1a2e', fontSize: '0.9rem' }}>
                    {meta?.icon} {meta?.label}
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#aaa', fontWeight: 400 }}>{snap.period}</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.2rem' }}>
                    {snap.followers && <span>Followers: <b>{formatNum(snap.followers)}</b>  </span>}
                    {snap.newFollowers != null && <span style={{ color: snap.newFollowers >= 0 ? '#2e7d32' : '#c62828' }}>
                      Growth: <b>{snap.newFollowers >= 0 ? '+' : ''}{formatNum(snap.newFollowers)}</b>  </span>}
                    {snap.totalViews && <span>Views: <b>{formatNum(snap.totalViews)}</b>  </span>}
                    {snap.engagementRate && <span>Eng: <b>{snap.engagementRate}%</b></span>}
                  </div>
                </div>
                <button onClick={() => handleDelete(snap.id)} style={{ ...iconBtn, color: '#e53e3e', flexShrink: 0 }} title="Delete">🗑️</button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Log Stats Modal ──────────────────────────────────────── */}
      {showForm && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#1a1a2e' }}>📊 Log Monthly Stats</h2>
              <button onClick={() => setShowForm(false)} style={closeBtn}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>

              <label style={labelStyle}>Platform
                <select value={form.platform} onChange={e => f({ platform: e.target.value })} style={input}>
                  {PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.icon} {p.label}</option>)}
                </select>
              </label>
              <label style={labelStyle}>Month
                <select value={form.period} onChange={e => f({ period: e.target.value })} style={input}>
                  {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>

              <div style={sectionLabel}>📈 Audience</div>

              <label style={labelStyle}>
                {PLATFORMS.find(p => p.key === form.platform)?.metric || 'Followers'} (Total)
                <input value={form.followers} onChange={e => f({ followers: e.target.value })} style={input} type="number" placeholder="e.g. 125000" />
              </label>
              <label style={labelStyle}>New this month (growth)
                <input value={form.newFollowers} onChange={e => f({ newFollowers: e.target.value })} style={input} type="number" placeholder="e.g. 3500 (can be negative)" />
              </label>

              <div style={sectionLabel}>👁️ Reach & Views</div>

              <label style={labelStyle}>Total Views / Reach this month
                <input value={form.totalViews} onChange={e => f({ totalViews: e.target.value })} style={input} type="number" placeholder="e.g. 450000" />
              </label>
              <label style={labelStyle}>Avg Views per Post/Video
                <input value={form.avgViewsPerPost} onChange={e => f({ avgViewsPerPost: e.target.value })} style={input} type="number" placeholder="e.g. 28000" />
              </label>
              <label style={labelStyle}>Estimated Reach (unique)
                <input value={form.estimatedReach} onChange={e => f({ estimatedReach: e.target.value })} style={input} type="number" placeholder="from platform insights" />
              </label>
              <label style={labelStyle}>Posts / Videos published
                <input value={form.totalPosts} onChange={e => f({ totalPosts: e.target.value })} style={input} type="number" placeholder="e.g. 8" />
              </label>

              <div style={sectionLabel}>❤️ Engagement</div>

              <label style={labelStyle}>Total Likes
                <input value={form.totalLikes} onChange={e => f({ totalLikes: e.target.value })} style={input} type="number" placeholder="e.g. 25000" />
              </label>
              <label style={labelStyle}>Total Comments
                <input value={form.totalComments} onChange={e => f({ totalComments: e.target.value })} style={input} type="number" placeholder="e.g. 1200" />
              </label>
              <label style={labelStyle}>Engagement Rate (%)
                <input value={form.engagementRate} onChange={e => f({ engagementRate: e.target.value })} style={input} type="number" step="0.1" placeholder="e.g. 4.5" />
              </label>

              <label style={{ ...labelStyle, gridColumn: '1/-1' }}>Notes
                <input value={form.notes} onChange={e => f({ notes: e.target.value })} style={input} placeholder="Any notable events this month (viral video, collab, etc.)" />
              </label>
            </div>

            <div style={{ background: '#fff8e1', borderRadius: 8, padding: '0.65rem', marginTop: '0.85rem', fontSize: '0.78rem', color: '#7a5c00' }}>
              📌 Where to find stats: YouTube Studio → Analytics · Instagram → Professional Dashboard · Twitter → Analytics.twitter.com
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={outlineBtn}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={primaryBtn}>
                {saving ? 'Saving…' : '💾 Save Stats'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CSS Bar Chart Component ────────────────────────────────────────────────────
function BarChart({ data, valueKey = 'followers', color = '#1a7a4a' }) {
  if (!data || data.length === 0) return null;

  const values = data.map(d => Number(d[valueKey] || 0));
  const maxVal = Math.max(...values, 1);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', height: 120, padding: '0 0.5rem' }}>
        {data.map((d, i) => {
          const val = Number(d[valueKey] || 0);
          const h   = Math.max((val / maxVal) * 100, 2);
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem' }}>
              <div style={{ fontSize: '0.65rem', color: '#888', textAlign: 'center' }}>{formatNum(val)}</div>
              <div style={{ width: '100%', height: `${h}%`, background: color, borderRadius: '4px 4px 0 0', opacity: 0.85, transition: 'height 0.3s', minHeight: 4 }} />
            </div>
          );
        })}
      </div>
      {/* X axis labels */}
      <div style={{ display: 'flex', gap: '0.5rem', padding: '0.3rem 0.5rem 0', borderTop: '1px solid #f0f0f0' }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.65rem', color: '#aaa' }}>
            {d.period?.slice(5)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatNum(n) {
  const num = Number(n || 0);
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000)     return (num / 1_000).toFixed(1)     + 'K';
  return num.toLocaleString('en-IN');
}

const summaryCard = (bg) => ({
  background: bg, borderRadius: 16, padding: '1.25rem',
  display: 'flex', flexDirection: 'column', gap: '0.2rem',
});

// Styles
const primaryBtn  = { background: '#1a7a4a', color: 'white', border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const outlineBtn  = { background: 'white', color: '#1a7a4a', border: '1.5px solid #1a7a4a', borderRadius: 10, padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const iconBtn     = { background: '#f5f5f5', border: 'none', borderRadius: 8, padding: '0.4rem 0.55rem', cursor: 'pointer', fontSize: '0.9rem' };
const input       = { padding: '0.55rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'white' };
const labelStyle  = { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: '#444' };
const sectionLabel = { gridColumn: '1/-1', fontWeight: 800, fontSize: '0.78rem', color: '#1a7a4a', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '2px solid #e8f5e9' };
const emptyState  = { textAlign: 'center', padding: '4rem 2rem', background: 'white', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const overlay     = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' };
const modal       = { background: 'white', borderRadius: 18, padding: '1.75rem', width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const closeBtn    = { background: '#f5f5f5', border: 'none', borderRadius: 8, padding: '0.4rem 0.7rem', cursor: 'pointer', fontSize: '1rem' };
